import { useMemo } from 'react';
import type { MediaSSR } from '@atlaskit/renderer';
import type { MediaContext } from '@atlassian/jira-issue-media-provider/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { mediaFeatureFlags } from './media-feature-flags.tsx';

const MEDIA_PROVIDER_UNAVAILABLE_ERROR = 'mediaProvider unavailable, must use initialAuth';

export type MediaContextProps = {
	mediaContext: MediaContext;
};

const getServerMediaOptions = ({ mediaContext }: MediaContextProps): MediaSSR => {
	return {
		mode: 'server',
		config: {
			authProvider: () => Promise.reject(new Error(MEDIA_PROVIDER_UNAVAILABLE_ERROR)),
			initialAuth: {
				clientId: mediaContext?.viewContext?.clientId || '',
				baseUrl: mediaContext?.viewContext?.serviceHost || '',
				token: mediaContext?.viewContext?.token || '',
			},
			enforceDataSecurityPolicy: false,
		},
	};
};

const getClientMediaOptions = ({ mediaContext }: MediaContextProps): MediaSSR => {
	return {
		mode: 'client',
		config: {
			authProvider: () =>
				Promise.resolve({
					clientId: mediaContext?.viewContext?.clientId || '',
					baseUrl: mediaContext?.viewContext?.serviceHost || '',
					token: mediaContext?.viewContext?.token || '',
				}),
			initialAuth: {
				clientId: mediaContext?.viewContext?.clientId || '',
				baseUrl: mediaContext?.viewContext?.serviceHost || '',
				token: mediaContext?.viewContext?.token || '',
			},
			enforceDataSecurityPolicy: false,
		},
	};
};

export const getMediaOptions = ({ mediaContext }: MediaContextProps) => {
	if (fg('jfp-magma-ssr-iv-editor-media')) {
		return {
			allowLinking: true,
			enableSyncMediaCard: true,
			featureFlags: mediaFeatureFlags(),
			allowPixelResizing: Boolean(fg('jfp-magma-ssr-iv-editor-media-pixel-width')),
			ssr: __SERVER__
				? getServerMediaOptions({ mediaContext })
				: getClientMediaOptions({ mediaContext }),
		};
	}

	// This is the current state of the media options.
	return {
		allowLinking: true,
		featureFlags: mediaFeatureFlags(),
		allowPixelResizing: Boolean(fg('jfp-magma-ssr-iv-editor-media-pixel-width')),
	};
};

export const useGetMediaOptions = ({ mediaContext }: MediaContextProps) => {
	return useMemo(() => getMediaOptions({ mediaContext }), [mediaContext]);
};
